import { decamelizeKeys } from "humps";
import isNil from "lodash/isNil";
import some from "lodash/some";
import { AvailabilityLimitWrapper } from "PFApp/components/filters/filter_items/availability_filter/parts/availability_limit_wrapper";
import { useBufferedFilter } from "PFApp/components/filters_v2/hooks/use_buffered_filter";
import { ThresholdSlider } from "PFComponents/availability/threshold_slider";
import { Tooltip } from "PFComponents/tooltip";
import { isAvailabilityRuleV2 } from "PFCore/helpers/filters";
import { AvailabilityRule, AvailabilityRuleV2, Template } from "PFTypes";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { useTranslation } from "react-i18next";

import { ReadableAvailabilityRules } from "../../../readable_availability_rules";
import css from "./filters_v2_availability_threshold_box.module.scss";

type AvailabilityFilterItemCompressedProps = {
  scope: number;
  template?: Template;
  disabled?: boolean;
};

export const FiltersV2AvailabilityThresholdBox = ({
  scope = 0,
  template,
  disabled
}: AvailabilityFilterItemCompressedProps) => {
  const { t } = useTranslation("filters");

  const { filter: availabilityFilter, update } = useBufferedFilter(KnownFiltersV2.Availability);

  if (!template || !availabilityFilter?.value) {
    return null;
  }

  const availability = availabilityFilter.value as AvailabilityRuleV2;

  const updateThreshold = (availabilityThreshold: number) => {
    update({
      value: {
        ...(isAvailabilityRuleV2(availabilityFilter.value) ? availabilityFilter.value : {}),
        availabilityThreshold
      } as AvailabilityRuleV2
    });
  };

  const isAvailabilitySet = some(availability.ranges, ({ start, end }) => !isNil(start) && !isNil(end));

  const threshold =
    availability.availabilityThreshold ?? template?.schema.properties?.availability?.default_threshold ?? 70;

  return (
    <AvailabilityLimitWrapper className={css.rootCompressed} scope={scope}>
      <ThresholdSlider
        threshold={threshold}
        disabled={disabled || !isAvailabilitySet}
        tooltip={
          !isAvailabilitySet
            ? t("availabilityRequirement.tooltips.addRequirementsBeforeThreshold")
            : undefined
        }
        onChange={updateThreshold}
      />
      {isAvailabilitySet && (
        <Tooltip
          content={
            <div className={css.availabilityTooltip}>
              <ReadableAvailabilityRules availability={decamelizeKeys(availability) as AvailabilityRule} />
            </div>
          }
          tooltipContainerProps={{ style: { maxWidth: 400 } }}
          interactive
        >
          <ReadableAvailabilityRules
            availability={decamelizeKeys(availability) as AvailabilityRule}
            rangesLimit={1}
          />
        </Tooltip>
      )}
    </AvailabilityLimitWrapper>
  );
};
